import { tracked } from "@glimmer/tracking";
import Service, { service } from "@ember/service";

const STORAGE_KEY_PREFIX = "community_navigation_game_order_v1";

export default class CommunityGameOrderService extends Service {
  @service currentUser;
  @service keyValueStore;

  @tracked order = [];

  availableGames = new Map();
  initialized = false;

  get storageKey() {
    return `${STORAGE_KEY_PREFIX}_${this.currentUser?.id ?? "anonymous"}`;
  }

  get games() {
    return this.order.map((key) => this.availableGames.get(key));
  }

  setGames(games) {
    this.availableGames = new Map(
      games
        .filter((game) => game.game_key)
        .map((game) => [game.game_key, game])
    );

    const storedOrder = this.initialized
      ? this.order
      : this.keyValueStore.getObject(this.storageKey);

    this.order = this.#reconcile(storedOrder);
    this.initialized = true;
  }

  move(gameKey, targetGameKey, placement) {
    if (
      gameKey === targetGameKey ||
      !this.availableGames.has(gameKey) ||
      !this.availableGames.has(targetGameKey)
    ) {
      return false;
    }

    const nextOrder = this.order.filter((key) => key !== gameKey);
    let targetIndex = nextOrder.indexOf(targetGameKey);
    if (placement === "after") {
      targetIndex += 1;
    }
    nextOrder.splice(targetIndex, 0, gameKey);

    if (nextOrder.every((key, index) => key === this.order[index])) {
      return false;
    }

    this.order = nextOrder;
    return true;
  }

  moveBy(gameKey, offset) {
    const currentIndex = this.order.indexOf(gameKey);
    const nextIndex = currentIndex + offset;
    if (
      currentIndex === -1 ||
      nextIndex < 0 ||
      nextIndex >= this.order.length
    ) {
      return;
    }

    const nextOrder = [...this.order];
    nextOrder.splice(currentIndex, 1);
    nextOrder.splice(nextIndex, 0, gameKey);
    this.order = nextOrder;
    return nextIndex;
  }

  persist() {
    this.keyValueStore.setObject({
      key: this.storageKey,
      value: this.order,
    });
  }

  #reconcile(storedOrder) {
    const availableKeys = [...this.availableGames.keys()];
    const available = new Set(availableKeys);
    const seen = new Set();
    const reconciled = [];

    if (Array.isArray(storedOrder)) {
      for (const key of storedOrder) {
        if (available.has(key) && !seen.has(key)) {
          reconciled.push(key);
          seen.add(key);
        }
      }
    }

    for (const key of availableKeys) {
      if (!seen.has(key)) {
        reconciled.push(key);
      }
    }

    return reconciled;
  }
}
